/**
 * SEE-1435 — scope Vista EM cost codes / cost types to the equipment's EM Department.
 *
 * Why this exists: the existing cascade scopes both lists to the selected
 * equipment's EMGroup, which only helps when a customer uses more than one group.
 * Griffith runs a single EMGroup across all 3,679 pieces of equipment, so the
 * filter matches everything and users see every cost code. Their EM Departments
 * are the only axis that discriminates — department 303H lists 4 cost codes
 * against the 38 in the group.
 *
 * Shared by the desktop dropdowns and the mobile coding menu so the fallback
 * semantics can't drift between them.
 */
import type {
  AgaveEmCostCodeOption,
  AgaveEmCostTypeOption,
  AgaveEmDepartmentOption,
} from 'graphql_custom/transactions/equipment-coding';

/**
 * Read the per-paidol opt-in. Absent means off — the setting is opt-in, so a
 * paidol that has never touched it keeps the EMGroup-only behaviour.
 *
 * Takes the minimal structural shape rather than the full `Paidol` so both the
 * redux `selectedPaidol` and the settings page's `paidol` satisfy it without a
 * cast at either call site.
 */
export const isEmDepartmentScopingEnabled = (
  paidol?: { scopeEquipmentCodesToEmDepartment?: { isEnabled: boolean } | null } | null
): boolean => Boolean(paidol?.scopeEquipmentCodesToEmDepartment?.isEnabled);

/** Minimal shape needed to build a group-qualified key. */
interface GroupScopedCode {
  emGroup?: string | null;
  code?: string | null;
}

/**
 * Find the department a piece of equipment belongs to.
 *
 * Departments are EM *company* scoped, so the match is on `(emCo, code)` — the
 * department code alone is ambiguous, since the same code exists under each EMCo
 * (our Vista sandbox has department "1" under both 201 and 202).
 */
export const findEmDepartment = (
  departments: AgaveEmDepartmentOption[],
  emCo?: string | null,
  departmentCode?: string | null
): AgaveEmDepartmentOption | undefined => {
  if (!emCo || !departmentCode) return undefined;
  return departments.find((d) => d.emCo === emCo && d.code === departmentCode);
};

/**
 * Narrow `items` to those the department declares, or return them untouched when
 * it declares nothing.
 *
 * Both null and empty mean "no restriction". Null is the never-synced case
 * (departments not pulled yet, or the customer's flow has no em-departments
 * step). Empty is Vista having no rows on that tab — which for cost codes is the
 * *normal* state, because the Cost Codes tab is an override table (view EMDO,
 * where a GL account is required per row) rather than a whitelist. Treating
 * empty as "allow nothing" would blank the dropdown for every customer who
 * doesn't use the tab, turning a narrowing feature into an outage.
 *
 * Keys are group-qualified (`"201:95"`) because a department belongs to one EMCo
 * which may span several EMGroups, while the codes resolve by `(EMGroup, code)`.
 */
export const scopeToDepartmentKeys = <T extends GroupScopedCode>(
  items: T[],
  keys?: (string | null)[] | null
): T[] => {
  if (!keys || keys.length === 0) return items;

  const allowed = new Set(keys.filter((k): k is string => !!k));
  if (allowed.size === 0) return items;

  const scoped = items.filter(
    (item) => item.emGroup != null && item.code != null && allowed.has(`${item.emGroup}:${item.code}`)
  );

  // If the department's keys match nothing in the synced lists, the two datasets
  // are out of step — a department referencing an EMGroup whose cost codes were
  // never synced, or a mid-sync window. Falling back beats presenting an empty
  // dropdown the user cannot act on.
  return scoped.length > 0 ? scoped : items;
};

/**
 * Cost codes the selected equipment may use: EMGroup first (existing behaviour),
 * then narrowed to the department's Cost Codes tab when it declares any.
 */
export const scopeCostCodesToDepartment = (
  inGroupCostCodes: AgaveEmCostCodeOption[],
  department?: AgaveEmDepartmentOption
): AgaveEmCostCodeOption[] => scopeToDepartmentKeys(inGroupCostCodes, department?.costCodeKeys);

/**
 * Cost types the selected equipment may use, narrowed by the department's Cost
 * Types tab. Applied *before* the SEE-975 cost-code-pair filter, which stays the
 * authoritative posting constraint ("Equipment cost posting will be limited to
 * the cost code/cost type combinations that you create using this form"); the
 * department list governs GL routing. Both apply, so they intersect.
 */
export const scopeCostTypesToDepartment = (
  inGroupCostTypes: AgaveEmCostTypeOption[],
  department?: AgaveEmDepartmentOption
): AgaveEmCostTypeOption[] => scopeToDepartmentKeys(inGroupCostTypes, department?.costTypeKeys);
